/* eslint-disable @nx/enforce-module-boundaries */
import React, { Fragment } from 'react';
import {
  AmexIcon,
  CardIcon,
  MastercardIcon,
  VisaIcon,
  CirclePlusBg,
  LockIcon,
} from '@mybridge/icons';
import {
  Box,
  Flex,
  HStack,
  Radio,
  Stack,
  VStack,
  Text,
  Center,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Heading,
  IconButton,
} from '@mybridge/ui';
import { Button } from '@mybridge/ui/button';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPremiumSubscriptionPlans,
  getPremiumSubscription,
  createPremiumSubscription,
  updatePremiumSubscription,
  deletePremiumSubscription,
} from 'v4/store/actions/myDisk.Action';
import _ from 'lodash';
import Link from 'next/link';
import { useToast } from '@chakra-ui/react';
import { cardList, validatePromoCode } from 'v4/store/actions/payments.actions';
import { Input, InputGroup, InputRightElement } from '@mybridge/ui/input';
import { CloseIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { useDisclosure } from '@chakra-ui/react';
import { getUserProfileInfo } from 'v4/store/actions/user-profile.actions';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

const PremiumSubscription = () => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const toast = useToast();
  const cancelDisc = useDisclosure();
  const periodDisc = useDisclosure();
  const { premiumSubscriptionPlans, premiumSubscriptionDetails } =
    useSelector((state) => state.myDisk) ?? {};
  const { cardInfo } = useSelector((state) => state.payment);
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const [premiumBilledAs, setPremiumBilledAs] = useState('price_monthly');
  const [activateSub, setActivateSub] = useState(false);
  const monthlyPrice = premiumSubscriptionPlans?.month_amount ?? 0;
  const yearlyPrice = premiumSubscriptionPlans?.year_amount ?? 0;
  const spentAmount = monthlyPrice * 12;
  const defaultCard = _.find(cardInfo?.cards, {
    id: cardInfo?.default_card_id,
  });

  const [showOfferInput, setShowOfferInput] = useState(false);
  const [offerCodeInput, setOfferCodeInput] = useState('');
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState('');

  const rawPrice = premiumBilledAs === 'price_monthly' ? monthlyPrice : yearlyPrice;
  const periodLabel = premiumBilledAs === 'price_monthly' ? '/mo' : '/yr';

  const handleApplyOffer = async () => {
    if (!offerCodeInput || !offerCodeInput.trim()) return;
    setOfferLoading(true);
    setOfferError('');
    try {
      const res = await dispatch(
        validatePromoCode({
          code: offerCodeInput.trim(),
          service_type: 'PREMIUMSUB',
          billed_type: premiumBilledAs === 'price_monthly' ? 'monthly' : 'yearly',
          amount: (rawPrice || 0).toFixed(2),
        })
      );
      if (res?.payload?.valid) {
        setAppliedOffer(res.payload);
        setOfferError('');
      } else {
        setOfferError(res?.payload?.message || 'Invalid offer code');
      }
    } catch (err) {
      setOfferError('Failed to validate offer code');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleRemoveOffer = () => {
    setAppliedOffer(null);
    setOfferCodeInput('');
    setOfferError('');
  };

  useEffect(() => {
    setAppliedOffer(null);
    setOfferCodeInput('');
    setOfferError('');
    setShowOfferInput(false);
  }, [premiumBilledAs]);
  useEffect(() => {
    dispatch(getPremiumSubscriptionPlans());
    dispatch(getPremiumSubscription());
    dispatch(cardList());
  }, []);

  const displayCardIcon = (brand) => {
    switch (brand) {
      case 'visa':
        return <VisaIcon />;
      case 'mastercard':
        return <MastercardIcon />;
      case 'amex':
        return <AmexIcon />;
      default:
        return <CardIcon />;
    }
  };

  const handlePaymentSubmit = async () => {
    const payload = {
      price_id:
        premiumBilledAs === 'price_monthly'
          ? premiumSubscriptionPlans?.month_price_id
          : premiumSubscriptionPlans?.year_price_id,
      interval: premiumBilledAs === 'price_monthly' ? 'monthly' : 'yearly',
      ...(appliedOffer?.valid && appliedOffer?.code ? { promo_code: appliedOffer.code } : {}),
    };
    if (premiumSubscriptionDetails?.id) {
      dispatch(updatePremiumSubscription(payload)).then((res) => {
        if (res) {

          toast({
            title: res?.payload?.message,
            status: 'success',
            isClosable: true,
          });
          setActivateSub(false);
          dispatch(getPremiumSubscription());
          dispatch(getUserProfileInfo());
        }
      });
    } else {
      dispatch(createPremiumSubscription(payload)).then((res) => {
        if (res) {

          toast({
            title: res?.payload?.detail,
            status: 'success',
            isClosable: true,
          });
          setActivateSub(false);
          dispatch(getPremiumSubscription());
          dispatch(getUserProfileInfo());
        }
      });
    }
    periodDisc.onClose();
    // console.log(payload);
  };

  const cancelCurrentSubscription = () => {
    dispatch(deletePremiumSubscription()).then((res) => {
      if (res) {

        toast({
          title: res.payload.message,
          status: 'success',
          isClosable: true,
        });
        setActivateSub(false);
        dispatch(getPremiumSubscription());
        dispatch(getUserProfileInfo());
      }
    });
    cancelDisc.onClose();
  };

  // console.log(premiumSubscriptionDetails);

  return (
    <>
      {!activateSub && premiumSubscriptionDetails?.status_code === 404 ? (
        <Fragment>
          <Text fontSize="lg" mb={8}>
            Stand out with Premium. Enjoy unlimited profile visibility, greater exposure, and exclusive member benefits.
          </Text>

          <Box
            bg="brandPrimary.100"
            p="20px"
            maxW={["100%", "60%"]}
            className="cut-corners-right-bottom"
            borderRadius="15px"
            transition="all 0.2s" 
          >
            <Button
              variant="primary"
              size="lg"
              borderRadius="25px"
              fontWeight="bold"
              mt={8}
              mb={5}
              onClick={() => setActivateSub(true)}
            >
              {t('premiumSubscription.tryPremiumFree') || "Try Premium Free"}
            </Button>
            <Text color="Black" fontSize="18px" fontWeight="600">
              {t('premiumSubscription.price') || "Price"}: <Box as="s">${monthlyPrice}*</Box> {t('premiumSubscription.oneMonthTrial') || "1-month free trial"}
            </Text>
            <Text mb={5}>
              {t('premiumSubscription.afterTrial') || "After your free month, pay as little as"} ${monthlyPrice}* / {t('premiumSubscription.monthAfter') || "month after"}. {t('premiumSubscription.cancelAnytime') || "Cancel anytime. We'll remind you 7 days before your trial ends."}
            </Text>

            <Button
              leftIcon={<LockIcon color="white" />}
              bg="brandPrimary.300"
              color="#fff"
              variant="primary"
              onClick={() => setActivateSub(true)}
            >
              {t('premiumSubscription.secureCheckout') || "Secure checkout"}
            </Button>
          </Box>
        </Fragment>
      ) : (
        <></>
      )}

      {/* Premium subscription checkout */}
      {activateSub && (
        <>
          <HStack
            spacing={8}
            py="15px"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <VStack alignItems="flex-start" gap={0}>
              <Radio
                value="price_monthly"
                onChange={(e) => setPremiumBilledAs(e.target.value)}
                name="billed_as"
                size="lg"
                me="20px"
                isChecked={premiumBilledAs === 'price_monthly'}
              >
                {t('settingsSubscription.billedMonthly') || "Billed monthly"}
              </Radio>
              <Text fontSize={["12px", "14px"]}>
                myBridge {t('premiumSubscription.premiumIs') || "Premium is"} ${monthlyPrice} {t('settingsSubscription.perMonth') || "per month"}.
              </Text>
              <Text fontSize={["12px", "14px"]}>
                {t('premiumSubscription.youllSpend') || "You'll spend"} ${spentAmount} {t('premiumSubscription.perYear') || "per year"}
              </Text>
            </VStack>
            <VStack alignItems="flex-start" gap={0}>
              <Radio
                value="price_yearly"
                onChange={(e) => setPremiumBilledAs(e.target.value)}
                name="billed_as"
                size="lg"
                isChecked={premiumBilledAs === 'price_yearly'}
              >
                {t('settingsSubscription.billedYearly') || "Billed yearly"}
              </Radio>
              <Text fontSize={["12px", "14px"]}>{t('premiumSubscription.yearlyPaymentOf') || "Yearly payment of"} ${yearlyPrice}</Text>
            </VStack>
          </HStack>
          <Box>
            <Text my={3} fontSize="18px" fontWeight="bold" color="#3D5A80">
              {t('premiumSubscription.premiumSubscription') || "Premium Subscription"}
            </Text>
            <Flex justifyContent="space-between" my="15px">
              <Box>
                <Text fontWeight="bold" textTransform="capitalize">
                  {premiumBilledAs === 'price_monthly' ? 'Monthly' : 'Yearly'}{' '}
                  {t('premiumSubscription.subscriptionType') || "Subscription"}
                </Text>
              </Box>
              <Text>
                {'$' +
                  (premiumBilledAs === 'price_monthly'
                    ? monthlyPrice
                    : yearlyPrice) +
                  (premiumBilledAs === 'price_monthly' ? '/mo' : '/yr')}
              </Text>
            </Flex>

            {appliedOffer?.valid ? (
              <>
                <Flex justifyContent="space-between" alignItems="center" my="10px">
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="green.600" fontWeight="medium">
                      {appliedOffer.name || appliedOffer.code} ({appliedOffer.code})
                    </Text>
                    <Box
                      as="button"
                      fontSize="xs"
                      color="gray.400"
                      _hover={{ color: 'red.500', textDecoration: 'underline' }}
                      onClick={handleRemoveOffer}
                    >
                      Remove
                    </Box>
                  </HStack>
                  <Text fontSize="sm" fontWeight="bold" color="green.600">
                    -{'$' + (appliedOffer.discount_amount || 0).toFixed(2) + periodLabel}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center" my="10px" pt="10px" borderTop="1px dashed" borderColor="gray.200">
                  <Text fontWeight="bold" fontSize="md" color="#333333">
                    Total Amount
                  </Text>
                  <Text fontWeight="bold" fontSize="md" color="green.600">
                    {'$' + (appliedOffer.final_amount != null ? Number(appliedOffer.final_amount).toFixed(2) : Math.max(0, rawPrice - (appliedOffer.discount_amount || 0)).toFixed(2)) + periodLabel}
                  </Text>
                </Flex>
              </>
            ) : !showOfferInput ? (
              <Box my="8px">
                <Box
                  as="button"
                  fontSize="sm"
                  color="green.600"
                  fontWeight="semibold"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => setShowOfferInput(true)}
                >
                  Apply Promo Code
                </Box>
              </Box>
            ) : (
              <Box my="8px">
                <InputGroup size="sm">
                  <Input
                    placeholder="Enter offer code"
                    value={offerCodeInput}
                    onChange={(e) => setOfferCodeInput(e.target.value)}
                    borderRadius="8px"
                    pr="75px"
                  />
                  <InputRightElement width="70px" pr="4px">
                    {offerCodeInput?.length ? (
                      <Button
                        size="xs"
                        variant="transparent"
                        isLoading={offerLoading}
                        onClick={handleApplyOffer}
                      >
                        Apply
                      </Button>
                    ) : (
                      <IconButton
                        size="xs"
                        variant="transparent"
                        icon={<CloseIcon width="10px" height="10px" color="gray.500" />}
                        onClick={() => {
                          setShowOfferInput(false);
                          setOfferCodeInput('');
                          setOfferError('');
                        }}
                      />
                    )}
                  </InputRightElement>
                </InputGroup>
                {offerError && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {offerError}
                  </Text>
                )}
              </Box>
            )}

            {defaultCard ? (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                my="15px"
              >
                <Box as="button" color="brandPrimary.500" fontSize={["sm", "md"]}>
                  {t('settingsSubscription.changeCard') || "Change Card"}
                </Box>
                <Flex>
                  {displayCardIcon(defaultCard?.card?.brand)}
                  <Box
                    as="span"
                    className="ms-2" fontSize={["sm", "md"]}
                  >{`${t('settingsSubscription.cardEndingIn') || "Card ending in"} ${defaultCard?.card?.last4}`}</Box>
                </Flex>
                <Box fontSize={["sm", "md"]}>{`${defaultCard?.card?.exp_month}/${defaultCard?.card?.exp_year}`}</Box>
              </Flex>
            ) : (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                my="15px"
              >
                <Link href="/settings/billing-and-payment">
                  <HStack>
                    <Text fontSize="16px" color="#333333">
                      {t('settingsSubscription.addNewCard') || "To continue, add New Credit or Debit card"}
                    </Text>{' '}
                    <CirclePlusBg />
                  </HStack>
                </Link>
              </Flex>
            )}

            <hr />
            <HStack w="100%" justifyContent="flex-end" mt="15px">
              <Button
                borderRadius="full"
                variant="primary"
                onClick={() => handlePaymentSubmit()}
                isDisabled={!defaultCard}
              >
                {t('myDisk.submit') || "Submit"}
              </Button>
            </HStack>
          </Box>
        </>
      )}

      {premiumSubscriptionDetails &&
      premiumSubscriptionDetails?.id &&
      !premiumSubscriptionDetails?.expired &&
      !activateSub ? (
        <>
          <Stack spacing="40px">
            <Stack  direction={['column', 'row']} gap="40px" justifyContent="space-between">
              <VStack alignItems="flex-start" flex="1">
                <Text fontWeight="700">{t('settingsSubscription.paymentMethod') || "Payment Method"}</Text>
                <HStack>
                  <Text>{t('settingsSubscription.cardEndingIn') || "Card ending in"} {defaultCard?.card?.last4}</Text>{' '}
                  <Center height="25px">
                    <Divider orientation="vertical" borderColor="#ABABAB" />
                  </Center>{' '}
                  <Link href="/settings/billing-and-payment">{t('settingsBilling.update') || "Update"}</Link>
                </HStack>
              </VStack>

              <VStack alignItems="flex-start" flex="1">
                <Text fontWeight="700">{t('premiumSubscription.billingPeriod') || "Billing Period"}</Text>
                <HStack>
                  <Text>
                    {t('premiumSubscription.planBilled') || "Plan billed"} {premiumSubscriptionDetails?.billed_type}
                  </Text>{' '}
                  <Center height="25px">
                    <Divider orientation="vertical" borderColor="#ABABAB" />
                  </Center>{' '}
                  <Button
                    variant="transparent"
                    p="0"
                    onClick={() => periodDisc.onOpen()}
                  >
                    {t('settingsBilling.update') || "Update"}
                  </Button>
                </HStack>
              </VStack>
              <VStack alignItems="flex-start" flex="1">
                <Text fontWeight="700">{t('pages.billingInformation') || "Billing Information"}</Text>
                <HStack>
                  {/* <Text>Plan billed monthly</Text>{' '}
                  <Center height="25px">
                    <Divider orientation="vertical" borderColor="#ABABAB" />
                  </Center>{' '} */}
                  <Link href="/settings/billing-and-payment">{t('settingsBilling.update') || "Update"}</Link>
                </HStack>
              </VStack>
            </Stack>

            <Stack direction={['column', 'row']} gap="40px" justifyContent="space-between">
              <VStack alignItems="flex-start" flex="1">
                <Text fontWeight="700">{t('premiumSubscription.lastPayment') || "Last Payment"}</Text>
                <HStack>
                  <Text>
                    {moment(premiumSubscriptionDetails?.updated_at).format(
                      'MMMM Do YYYY'
                    )}
                  </Text>{' '}
                  <Center height="25px">
                    <Divider orientation="vertical" borderColor="#ABABAB" />
                  </Center>{' '}
                  <Text>
                    {premiumSubscriptionDetails?.expired
                      ? t('settingsPermission.inactive') || 'Inactive'
                      : t('settingsPermission.active') || 'Active'}
                  </Text>
                </HStack>
              </VStack>

              <VStack alignItems="flex-start" flex="1">
                <Text fontWeight="700">{t('premiumSubscription.nextPayment') || "Next Payment"}</Text>
                <HStack>
                  <Text>
                    {moment(premiumSubscriptionDetails?.expires_at).format(
                      'MMMM Do YYYY'
                    )}
                  </Text>{' '}
                  <Center height="25px">
                    <Divider orientation="vertical" borderColor="#ABABAB" />
                  </Center>{' '}
                  <Text>
                    $
                    {premiumSubscriptionDetails?.billed_type === 'monthly'
                      ? monthlyPrice
                      : yearlyPrice}
                  </Text>
                </HStack>
              </VStack>
              <VStack alignItems="flex-start" flex="1">
                <Text fontWeight="700">{t('premiumSubscription.cancelSubscription') || "Cancel Subscription"}</Text>
                <HStack>
                  {/* <Text>Plan billed monthly</Text>{' '}
                  <Center height="25px">
                    <Divider orientation="vertical" borderColor="#ABABAB" />
                  </Center>{' '} */}
                  <Button
                    variant="transparent"
                    p="0"
                    onClick={() => cancelDisc.onOpen()}
                  >
                    {t('buttons.cancel') || "Cancel"}
                  </Button>
                </HStack>
              </VStack>
            </Stack>
          </Stack>
        </>
      ) : (
        <></>
      )}
      {/*Delete confirmation box*/}
      <AlertDialog {...cancelDisc} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('premiumSubscription.cancelPremiumSubscription') || "Cancel Premium Subscription"}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('premiumSubscription.cancelSubscriptionConfirm') || "Are you sure? You want to cancel this subscription."}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={cancelDisc.onClose}>{t('buttons.cancel') || "Cancel"}</Button>
              <Button
                colorScheme="red"
                onClick={cancelCurrentSubscription}
                ml={3}
              >
                {t('myEvents.yes') || "Yes"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal {...periodDisc} size="2xl">
        <ModalOverlay />
        <ModalContent>
          {/* <ModalHeader>Modal Title</ModalHeader> */}
          <ModalCloseButton />
          <ModalBody>
            <Heading
              as="h3"
              fontSize="22px"
              color="brandPrimary.500"
              textAlign="center"
              mt="30px"
            >
              {t('premiumSubscription.changeBillingPeriod') || "Change billing period"}
            </Heading>

            <Divider orientation="horizontal" maxW="100px" m="30px auto" />

            <Stack spacing="16px">
              <Box
                p="10px"
                borderRadius="8px"
                border="1px solid"
                borderColor="brandPrimary.500"
              >
                <HStack>
                  <VStack alignItems="space-between" gap={0} width="100%">
                    <Radio
                      value="price_monthly"
                      onChange={(e) => setPremiumBilledAs(e.target.value)}
                      name="billed_as"
                      size="lg"
                      me="20px"
                      isChecked={premiumBilledAs === 'price_monthly'}
                    >
                      <Text fontWeight="bold">{t('settingsSubscription.billedMonthly') || "Billed monthly"}</Text>
                    </Radio>
                    <Text fontSize="12px">
                      myBridge {t('premiumSubscription.premiumIs') || "Premium is"} ${monthlyPrice} {t('settingsSubscription.perMonth') || "per month"}. {t('premiumSubscription.youllSpend') || "You'll spend"} ${spentAmount} {t('premiumSubscription.perYear') || "per year"}
                    </Text>
                  </VStack>
                  <VStack alignItems="center" width="200px" gap="0">
                    <Text
                      fontSize="22px"
                      fontWeight="bold"
                      color="brandPrimary.500"
                      lineHeight="20px"
                    >
                      ${monthlyPrice}
                    </Text>
                    <Text>{t('settingsSubscription.perMonth') || "per month"}</Text>
                  </VStack>
                </HStack>
              </Box>

              <Box
                p="10px"
                borderRadius="8px"
                border="1px solid"
                borderColor="brandPrimary.500"
              >
                <HStack>
                  <VStack alignItems="space-between" gap={0} width="100%">
                    <Radio
                      value="price_yearly"
                      onChange={(e) => setPremiumBilledAs(e.target.value)}
                      name="billed_as"
                      size="lg"
                      me="20px"
                      isChecked={premiumBilledAs === 'price_yearly'}
                    >
                      <Text fontWeight="bold">{t('settingsSubscription.billedYearly') || "Billed yearly"}</Text>
                    </Radio>
                    <Text fontSize="12px">
                      myBridge {t('premiumSubscription.premiumIs') || "Premium is"} ${yearlyPrice} {t('settingsSubscription.perMonth') || "per month"}.
                    </Text>
                  </VStack>
                  <VStack alignItems="center" width="200px" gap="0">
                    <Text
                      fontSize="22px"
                      fontWeight="bold"
                      color="brandPrimary.500"
                      lineHeight="20px"
                    >
                      ${yearlyPrice}
                    </Text>
                    <Text>{t('premiumSubscription.perYear') || "per year"}</Text>
                  </VStack>
                </HStack>
              </Box>
              <Button variant="primary" m="30px auto" size="lg" onClick={handlePaymentSubmit}>{t('premiumSubscription.confirmChange') || "Confirm Change"}</Button>
            </Stack>

            
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PremiumSubscription;
